import React, { useContext } from "react";
import { useTheme } from "@mui/material";
import {
  Button,
  Typography,
  Box,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
  Chip,
  Badge,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { keccak256, AbiCoder, zeroPadValue, Contract } from "ethers";
import energyAbi from "../abis/ErgonEnergy.json";

import { LogContext, ProviderContext } from "./ProvenanceHome";

const READING_API_BASE =
  process.env.REACT_APP_READING_API_BASE ||
  process.env.REACT_APP_PROVENANCE_API_BASE ||
  "https://serenity.ing";
const ZERO_HASH = "0x" + "0".repeat(64);
const ZERO_HASH_RE = /^0x0{64}$/i;

const isZeroHash = (h) => typeof h === "string" && ZERO_HASH_RE.test(h);


function VerifyActionButton({
  status,            // null | "pending" | true | false | "unknown" | "error"
  children,
  ...btnProps
}) {
  let badge = null;

  if (status === "pending") {
    badge = <CircularProgress size={12} />;
  } else if (status === true) {
    badge = <CheckCircleIcon fontSize="small" sx={{ color: "success.main" }} />;
  } else if (status === false) {
    badge = <CancelIcon fontSize="small" sx={{ color: "error.main" }} />;
  } else if (status === "unknown") {
    badge = <HelpOutlineIcon fontSize="small" sx={{ color: "text.disabled" }} />;
  } else if (status === "error") {
    badge = <CancelIcon fontSize="small" sx={{ color: "error.main" }} />;
  }

  return (
    <Badge
      overlap="rectangular"
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      badgeContent={badge}
      sx={{
        "& .MuiBadge-badge": {
          background: "transparent",
          minWidth: 0,
          height: "auto",
          right: 6,
          top: 6,
          padding: 0,
        },
      }}
    >
      <Button {...btnProps}>{children}</Button>
    </Badge>
  );
}


function unwrapApiPayload(resp) {
  if (!resp) return null;
  // single endpoint returns: { payload: <stored_payload> }
  if (resp.payload && typeof resp.payload === "object") return resp.payload;
  return resp;
}

function extractMeterReading(obj) {
  if (!obj || typeof obj !== "object") return null;
  if (obj.meter_reading && typeof obj.meter_reading === "object")
    return obj.meter_reading;
  if (obj.meterReading && typeof obj.meterReading === "object")
    return obj.meterReading;
  if (obj.payload && typeof obj.payload === "object") {
    const inner = obj.payload;
    if (inner.meter_reading && typeof inner.meter_reading === "object")
      return inner.meter_reading;
    if (inner.meterReading && typeof inner.meterReading === "object")
      return inner.meterReading;
  }
  return null;
}

function normalizeChainItem(raw) {
  const unwrapped = unwrapApiPayload(raw) || raw;
  const meter =
    extractMeterReading(unwrapped) || extractMeterReading(raw) || {};
  // Preserve everything; just ensure meter_reading exists for UI.
  if (unwrapped && typeof unwrapped === "object") {
    return {
      ...unwrapped,
      meter_reading: unwrapped.meter_reading || unwrapped.meterReading || meter,
      _raw: raw,
    };
  }
  return { meter_reading: meter, _raw: raw };
}

function getReadingHashFromItem(item) {
  const m = item?.meter_reading || {};
  return (
    m.reading_hash ||
    m.readingHash ||
    item?.reading_hash ||
    item?.readingHash ||
    ""
  ).toString();
}

function getPrevHashFromItem(item) {
  const m = item?.meter_reading || {};
  return (
    m.previous_reading_hash ||
    m.previousReadingHash ||
    item?.previous_reading_hash ||
    item?.previousReadingHash ||
    ""
  ).toString();
}

function getTimestampFromItem(item) {
  const m = item?.meter_reading || {};
  return (m.timestamp || item?.timestamp || "").toString();
}


function getTxHashFromItem(item) {
  const m = item?.meter_reading || {};
  return (m.tx_hash || m.txHash || item?.tx_hash || item?.txHash || "")
    .toString()
    .trim();
}

function findChainItemByReadingHash(chainItems, readingHash) {
  if (!Array.isArray(chainItems) || !readingHash) return null;
  const rh = String(readingHash).toLowerCase();
  return (
    chainItems.find((it) => getReadingHashFromItem(it).toLowerCase() === rh) ||
    null
  );
}


function pickEventByReadingHash(readingHash) {
  if (!readingHash) return null;
  const list = window.__provenanceEvents;
  if (!Array.isArray(list)) return null;
  const rh = readingHash.toLowerCase();
  return (
    list.find(
      (e) =>
        String(e?.args?.readingHash ||
            e?.args?.reading_hash ||
            e?.readingHash ||
            e?.reading_hash ||
            "").toLowerCase() === rh,
    ) || null
  );
}

function pickDeviceFromEvent(ev) {
  if (!ev) return "";
  const a = ev.args || {};
  return String(a.device || a.hepekID || a.hepek || a.primaryDevice || a[0] || "").trim();
}

function pickUserlistRoot(u) {
  if (!u) return "";
  // ethers returns a struct-like object with both numeric and named fields (depending on ABI)
  return String(u.root || u.rootReadingHash || u[11] || u["11"] || "").trim();
}

function getGenesisHashFromChain(items) {
  if (!Array.isArray(items) || items.length === 0) return "";

  // Prefer the element whose previous hash is ZERO (explicit genesis marker)
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const it = items[i];
    const prev = getPrevHashFromItem(it);
    if (prev && isZeroHash(prev)) {
      return getReadingHashFromItem(it);
    }
    const dir = String(it?.meter_reading?.direction || "").toUpperCase();
    if (dir === "GENESIS") {
      return getReadingHashFromItem(it);
    }
  }

  // Fallback: last element
  return getReadingHashFromItem(items[items.length - 1]);
}


async function fetchEnergyEventArgsFromTxHash(
  provider,
  contractAddress,
  abi,
  txHash,
  expectedReadingHash,
) {
  if (!provider || !contractAddress || !abi || !txHash) return null;

  const expected = expectedReadingHash
    ? String(expectedReadingHash).toLowerCase()
    : "";

    // Ensure txh has 0x prefix
    if (txHash && !txHash.startsWith("0x")) {
      txHash = "0x" + txHash;
    }


  const contract = new Contract(contractAddress, abi, provider);
  const receipt = await provider.getTransactionReceipt(txHash);
  const logs = receipt?.logs;
  if (!Array.isArray(logs) || logs.length === 0) return null;

  const addrLower = String(contractAddress).toLowerCase();

  for (const lg of logs) {
    try {
      if (!lg?.address) continue;
      if (String(lg.address).toLowerCase() !== addrLower) continue;

      const parsed = contract.interface.parseLog(lg);
      const args = parsed?.args;
      if (!args) continue;

      const rh = String(args.readingHash || args.reading_hash || args[2] || "")
        .toLowerCase();

      if (!expected || rh === expected) return { name: parsed.name, args };
    } catch {
      // ignore non-matching logs
    }
  }

  return null;
}


export default function AuditChainChase({
  readingHash,
  deviceAddress = "",
  txHash = "",
}) {
  const theme = useTheme();
  const [auditChain, setAuditChain] = React.useState(null);
  const [chainMeta, setChainMeta] = React.useState({
    reached_genesis: false,
    broken: false,
    missing_hash: "",
    loop_detected: false,
    depth: 0,
  });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [progressPhase, setProgressPhase] = React.useState("");
  const [progressHops, setProgressHops] = React.useState(0);

  const [eventDialog, setEventDialog] = React.useState({
    open: false,
    event: null,
  });
  const [traverseDialog, setTraverseDialog] = React.useState({
    open: false,
    chain: [],
    error: null,
  });

  // Track provenance verification results per reading_hash
  const [provenanceResults, setProvenanceResults] = React.useState({});
  const [lastEventName, setLastEventName] = React.useState(null);

  // Genesis anchor verification
  const [anchor, setAnchor] = React.useState({
    loading: false,
    ok: null,
    error: null,
    root: "",
    genesis: "",
    device: "",
  });

  // Tx sender verification per reading hash
  const [senderResults, setSenderResults] = React.useState({}); // rh -> 'pending' | true | false | 'unknown' | 'error'

  const { addLog, log } = useContext(LogContext);
  const provider = useContext(ProviderContext);

  const resolveDeviceAddressForChain = React.useCallback(
    (chainItems) => {
      const candidate = String(deviceAddress || "").trim();
      if (candidate && candidate.startsWith("0x") && candidate.length === 42) {
        return candidate;
      }

      // Try from the starting event (already in memory)
      const ev = pickEventByReadingHash(readingHash);
      const d = pickDeviceFromEvent(ev);
      if (d && d.startsWith("0x") && d.length === 42) return d;

      // Try from any chain item event
      if (Array.isArray(chainItems) && chainItems.length) {
        for (const it of chainItems) {
          const rh = getReadingHashFromItem(it);
          const ev2 = pickEventByReadingHash(rh);
          const d2 = pickDeviceFromEvent(ev2);
          if (d2 && d2.startsWith("0x") && d2.length === 42) return d2;
        }
      }

      return "";
    },
    [deviceAddress, readingHash],
  );
      
  // Show event dialog for a single readingHash
  const showEventDialog = async (hash) => {
    try {
      const res = await fetch(`${READING_API_BASE}/device/reading/hash/${hash}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // Try to extract eventName from data, fallback to null
      let eventName = null;
      if (data.eventName) eventName = data.eventName;
      else if (data.payload && data.payload.eventName)
        eventName = data.payload.eventName;
      else if (data.type) eventName = data.type;

      setEventDialog({ open: true, event: data });
      setLastEventName(eventName);
    } catch (e) {
      setEventDialog({ open: true, event: { error: e.message } });
      setLastEventName(null);
    }
  };

  // Recursively traverse the hash chain (single endpoint) from a given readingHash
  const traverseHashChain = async (startHash) => {
    const chain = [];
    let currentHash = startHash;
    let hops = 0;
    try {
      while (currentHash && hops < 15000) {
        const res = await fetch(
          `${READING_API_BASE}/device/reading/hash/${currentHash}`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        chain.push(data);

        const p = unwrapApiPayload(data) || {};
        const m = extractMeterReading(p) || {};
        const prev = String(m.previous_reading_hash ||
            m.previousReadingHash ||
            p.previous_reading_hash ||
            "").trim();

        if (!prev || isZeroHash(prev)) break;
        currentHash = prev;
        hops += 1;
      }
      setTraverseDialog({ open: true, chain, error: null });
    } catch (e) {
      setTraverseDialog({ open: true, chain, error: e.message });
    }
  };

  async function verifyProvenanceHashFromEvent(event) {
    try {
      const args = event?.args || event;
      if (!args) throw new Error("missing event args");

      const types = [
        // ProvenanceA struct
        "tuple(address hepekID, address userID, bytes32 readingHash, bytes32 prevProvenance, uint256 producedPower, uint256 meters)",
        // ProvenanceB struct
        "tuple(uint256 meterOutTotal, uint256 meterInTotal, uint256 ergonPrice, uint256 carbonPrice, uint256 tresholdLimit, uint256 bucketTs, uint256 blockNumber)"
      ];

      const values = [
        [
          args[0], // hepekID
          args[1], // userID
          args[2], // readingHash
          args[4], // prevProvenance
          args[5], // producedPower
          args[6], // meters
        ],
        [
          args[7],  // meterOutTotal
          args[8],  // meterInTotal
          args[9],  // ergonPrice
          args[10], // carbonPrice
          args[11], // tresholdLimit
          args[12], // bucketTs
          args[13], // blockNumber
        ]
      ];


      const encoded = new AbiCoder().encode(types, values);
      const calculated = keccak256(encoded);

      if (calculated.toLowerCase() === String(args[3]).toLowerCase()) {
        addLog && addLog(`provenanceHash OK for readingHash ${args[2]}`);
        return true;
      }
      addLog &&
        addLog(
          `provenanceHash MISMATCH for readingHash ${args[2]}: expected ${args[3]}, got ${calculated}`,
        );
      return false;
    } catch (e) {
      addLog && addLog(`provenanceHash verification ERROR: ${e.message}`);
      return false;
    }
  }

  // Export log to CSV (comma delimited)
  const exportLogToCSV = () => {
    if (!log || !Array.isArray(log) || log.length === 0) return;
    const rows = [
      ["Index", "Message"],
      ...log.map((msg, idx) => [
        idx + 1,
        String(msg)
          .replace(/\r?\n|\r/g, " ")
          .replace(/"/g, '""'),
      ]),
    ];
    const csvContent = rows
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `audit_log_${new Date().toISOString().replace(/[:.]/g, "-")}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const computeMeta = React.useCallback(async (items) => {
    const visited = new Set();
    let loop_detected = false;

    const hashes = [];
    for (const it of items) {
      const rh = getReadingHashFromItem(it);
      if (!rh) continue;
      if (visited.has(rh.toLowerCase())) loop_detected = true;
      visited.add(rh.toLowerCase());
      hashes.push(rh);
    }

    const last = items[items.length - 1];
    const lastPrev = getPrevHashFromItem(last);
    const reached_genesis = isZeroHash(lastPrev);
    const broken = !reached_genesis;
    const missing_hash = broken ? lastPrev || "" : "";

    return {
      reached_genesis,
      broken,
      missing_hash,
      loop_detected,
      depth: items.length,
    };
  }, []);

  const extendChainIfNeeded = React.useCallback(async (items, onHop) => {
    // If /all endpoint truncates early, we can extend using single endpoint.
    const visited = new Set(
      items.map((x) => getReadingHashFromItem(x).toLowerCase()).filter(Boolean),
    );
    let last = items[items.length - 1];
    let prev = getPrevHashFromItem(last);

    let hops = 0;
    while (prev && !isZeroHash(prev) && hops < 15000) {
      const key = prev.toLowerCase();
      if (visited.has(key)) break;
      visited.add(key);

      const res = await fetch(`${READING_API_BASE}/device/reading/hash/${prev}`);
      if (!res.ok) break;

      const data = await res.json();
      const normalized = normalizeChainItem(
        data?.payload ? data : unwrapApiPayload(data),
      );
      items.push(normalized);
      if (typeof onHop === "function") onHop(hops + 1);

      last = normalized;
      prev = getPrevHashFromItem(last);
      hops += 1;
    }
    return items;
  }, []);

  const fetchAuditChain = async () => {
    setLoading(true);
    setProgressPhase("Loading initial chain batch...");
    setProgressHops(0);
    setError(null);
    setAuditChain(null);
    setChainMeta({
      reached_genesis: false,
      broken: false,
      missing_hash: "",
      loop_detected: false,
      depth: 0,
    });
    setAnchor({
      loading: false,
      ok: null,
      error: null,
      root: "",
      genesis: "",
      device: "",
    });

    try {
      const res = await fetch(
        `${READING_API_BASE}/device/reading/hash/${readingHash}/all`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const payloads = Array.isArray(data) ? data : data.payloads;

      const normalized = Array.isArray(payloads)
        ? payloads.map(normalizeChainItem)
        : [];
      setProgressPhase(
        `Extending chain from initial batch (${normalized.length} records)...`,
      );
      const extended = await extendChainIfNeeded(normalized, (hopCount) => {
        setProgressHops(hopCount);
      });

      if (Array.isArray(extended)) {
        extended.forEach((payload, idx) => {
          addLog &&
            addLog(
              `Chase Audit Chain payload [${extended.length - idx}/${extended.length}]:\n` +
                JSON.stringify(payload, null, 2),
            );
        });
      }

      addLog &&
        addLog(
          `Chase Audit Chain: success (${extended.length} records) for readingHash ${readingHash}`,
        );

      const meta = await computeMeta(extended);
      setProgressPhase("Computing integrity checks...");
      setChainMeta(meta);
      setAuditChain(extended);

      if (meta.broken && meta.missing_hash) {
        addLog &&
          addLog(
            `Chase Audit Chain: BROKEN (missing prev=${meta.missing_hash})`,
          );
      }
      if (meta.loop_detected) {
        addLog &&
          addLog(
            `Chase Audit Chain: LOOP DETECTED (duplicate reading_hash found)`,
          );
      }

      // Auto-run genesis anchor verification so the GENESIS row can show ROOT MATCH/MISMATCH.
      // This does NOT rely on tx sender for GENESIS (commissioning tx is typically sent by an admin/owner).
      if (provider) {
        setProgressPhase("Verifying genesis anchor...");
        verifyGenesisAnchor(extended);
      }
      setProgressPhase("Done");
    } catch (e) {
      addLog &&
        addLog(
          `Chase Audit Chain: ERROR for readingHash ${readingHash}: ${e.message}`,
        );
      setError(e.message || "Error fetching audit chain");
    } finally {
      setLoading(false);
      setTimeout(() => {
        setProgressPhase("");
        setProgressHops(0);
      }, 250);
    }
  };

  const verifyGenesisAnchor = async (chainItems = auditChain) => {
    setAnchor({
      loading: true,
      ok: null,
      error: null,
      root: "",
      genesis: "",
      device: "",
    });
    try {
      if (!provider)
        throw new Error("provider not available (sync/connect first)");
      const contractAddress =
        process.env.REACT_APP_SOURCE_ERGONENERGY_CONTRACT_ADDRESS;
      if (!contractAddress)
        throw new Error(
          "Missing REACT_APP_SOURCE_ERGONENERGY_CONTRACT_ADDRESS",
        );

      const device = resolveDeviceAddressForChain(chainItems);
      if (!device) throw new Error("Cannot resolve device address (hepek)");

      if (!chainItems || !Array.isArray(chainItems) || chainItems.length === 0)
        throw new Error("audit chain not loaded");

      const genesisHash = getGenesisHashFromChain(chainItems);

      const contract = new Contract(contractAddress, energyAbi.abi, provider);
      const u = await contract.userlist(device);
      const root = pickUserlistRoot(u);

      const ok = Boolean(
        root && genesisHash && root.toLowerCase() === genesisHash.toLowerCase(),
      );
      setAnchor({
        loading: false,
        ok,
        error: null,
        root,
        genesis: genesisHash,
        device,
      });

      if (ok)
        addLog && addLog(`Genesis anchor MATCH: device=${device} root=${root}`);
      else
        addLog &&
          addLog(
            `Genesis anchor MISMATCH: device=${device} chainGenesis=${genesisHash} root=${root}`,
          );
    } catch (e) {
      setAnchor({
        loading: false,
        ok: null,
        error: e.message || String(e),
        root: "",
        genesis: "",
        device: "",
      });
      addLog &&
        addLog(`Genesis anchor verification ERROR: ${e.message || String(e)}`);
    }
  };

  
  const verifyTxSenderForReading = async (hash) => {
    setSenderResults((prev) => ({ ...prev, [hash]: "pending" }));
    try {
      if (!provider) throw new Error("provider not available");

      // Prefer in-memory event (fast, accurate for current sync window)
      const ev = pickEventByReadingHash(hash);

      // Expected sender is the HEPEK/device address (not the site/info string).
      const expected =
        (ev && pickDeviceFromEvent(ev)) ||
        resolveDeviceAddressForChain(auditChain) ||
        String(deviceAddress || "").trim();

      if (!expected || !expected.startsWith("0x") || expected.length !== 42) {
        setSenderResults((prev) => ({ ...prev, [hash]: "unknown" }));
        addLog &&
          addLog(
            `Tx sender check NOT CHECKED for ${hash}: cannot resolve expected hepek/device address.`,
          );
        return;
      }

      // Determine tx hash
      let txh = "";
      if (ev && ev.transactionHash) {
        txh = String(ev.transactionHash).trim();
      } else {
        // Fallback: read tx_hash from DB payload (meter_reading.tx_hash)
        const it = findChainItemByReadingHash(auditChain, hash);
        txh = getTxHashFromItem(it);
      }

      // Last fallback: only for the starting hash (detail dialog passes txHash)
      if (
        !txh &&
        String(hash).toLowerCase() === String(readingHash).toLowerCase()
      ) {
        txh = String(txHash || "").trim();
      }

      // Ensure txh has 0x prefix
      if (txh && !txh.startsWith("0x")) {
        txh = "0x" + txh;
      }

      if (!txh) {
        setSenderResults((prev) => ({ ...prev, [hash]: "unknown" }));
        addLog &&
          addLog(
            `Tx sender check NOT CHECKED for ${hash}: missing tx_hash (not in current events; DB payload has no tx_hash).`,
          );
        return;
      }

      const tx = await provider.getTransaction(txh);
      const from = String(tx?.from || "").toLowerCase();
      const ok = from && from === expected.toLowerCase();

      setSenderResults((prev) => ({ ...prev, [hash]: ok }));
      addLog &&
        addLog(
          `Tx sender check for ${hash}: from=${from} expected=${expected} => ${
            ok ? "MATCH" : "MISMATCH"
          }`,
        );
    } catch (e) {
      setSenderResults((prev) => ({ ...prev, [hash]: "error" }));
      addLog &&
        addLog(
          `Tx sender check ERROR for ${hash}: ${e.message || String(e)}`,
        );
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Button
        size="small"
        variant="outlined"
        color="primary"
        onClick={fetchAuditChain}
        disabled={loading}
        sx={{ mr: 1 }}
      >
        {loading ? "Loading audit chain..." : "Chase Audit Chain"}
      </Button>

      <Button
        size="small"
        variant="outlined"
        color="info"
        onClick={() => showEventDialog(readingHash)}
        sx={{ mr: 1 }}
      >
        Show Event (readingHash)
      </Button>

      <Button
        size="small"
        variant="outlined"
        color="secondary"
        onClick={exportLogToCSV}
        sx={{ mr: 1 }}
      >
        Export Log to CSV
      </Button>

      {auditChain && Array.isArray(auditChain) && provider && (
        <Button
          size="small"
          variant="outlined"
          color="success"
          onClick={() => verifyGenesisAnchor()}   // ✅ ignore event arg
          disabled={anchor.loading}
          sx={{ mr: 1 }}
        >
          {anchor.loading ? "Checking anchor..." : "Verify Genesis Anchor"}
        </Button>
      )}

      {anchor.ok === true && (
        <Chip
          size="small"
          variant="outlined"
          label="ANCHOR MATCH"
          color="success"
          sx={{ mt: 1, ml: 1 }}
        />
      )}
      {anchor.ok === false && (
        <Chip
          size="small"
          variant="outlined"
          label="ANCHOR MISMATCH"
          color="error"
          sx={{ ml: 1 }}
        />
      )}
      {anchor.error && (
        <Typography color="error" variant="caption" sx={{ ml: 2 }}>
          {anchor.error}
        </Typography>
      )}

      {/* Progress bar for lengthy fetchAuditChain action */}
      {loading && (
        <Box sx={{ width: "100%", mt: 1, mb: 1 }}>
          <LinearProgress />
          <Typography variant="caption" sx={{ ml: 1 }}>
            {progressPhase || "Fetching audit chain, please wait..."}
            {progressHops > 0 ? ` (extra hops fetched: ${progressHops})` : ""}
          </Typography>
        </Box>
      )}

      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}

      {auditChain && Array.isArray(auditChain) && (
        <Box
          sx={{
            mt: 2,
            maxHeight: 320,
            overflow: "auto",
            bgcolor: "rgba(0,0,0,0.03)",
            p: 1,
            borderRadius: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Audit Chain ({auditChain.length} readings)
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Chip
                size="small"
                label={chainMeta.reached_genesis ? "GENESIS" : "NOT AT GENESIS"}
                color={chainMeta.reached_genesis ? "success" : "warning"}
              />
              {chainMeta.broken && chainMeta.missing_hash && (
                <Chip size="small" label="CHAIN BROKEN" color="error" />
              )}
              {chainMeta.loop_detected && (
                <Chip size="small" label="LOOP DETECTED" color="error" />
              )}
              <Typography variant="caption" color="text.secondary">
                depth={chainMeta.depth}
                {chainMeta.broken && chainMeta.missing_hash
                  ? ` missing=${chainMeta.missing_hash}`
                  : ""}
              </Typography>
            </Box>
          </Box>

          <ol
            style={{ fontSize: 12, fontFamily: "monospace", paddingLeft: 20 }}
          >
            {auditChain.map((payload, idx) => {
              const r = payload?.meter_reading || {};
              const rh = String(r.reading_hash || r.readingHash || idx);
              const prev = String(r.previous_reading_hash || r.previousReadingHash || "");
              const ts = String(r.timestamp || "");

              const dir = String(r.direction || "").toUpperCase();
              const isGenesisRow = dir === "GENESIS" || isZeroHash(prev);
              const showProvButton = !isGenesisRow && Boolean(r.provenance_hash);
              const eventName = payload?.eventName || lastEventName || null;

              const senderState = senderResults[rh];
              const isAnchorRow =
                isGenesisRow &&
                anchor?.genesis &&
                String(anchor.genesis).toLowerCase() === rh.toLowerCase();

              return (
                <li key={rh}>
                  <div>
                    <b>reading_hash:</b> {rh}
                  </div>
                  <div>
                    <b>previous_reading_hash:</b> {prev}
                  </div>
                  <div>
                    <b>timestamp:</b> {ts}
                  </div>

                  <Button
                    size="small"
                    variant="outlined"
                    color="info"
                    onClick={() => showEventDialog(rh)}
                    sx={{ mr: 1, mt: 0.5, mb: 0.5 }}
                  >
                    Show Event
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    onClick={() => traverseHashChain(rh)}
                    sx={{ mr: 1, mt: 0.5, mb: 0.5 }}
                  >
                    Traverse Chain
                  </Button>

                  {!isGenesisRow ? (
                    <VerifyActionButton
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => verifyTxSenderForReading(rh)}
                      disabled={!provider || senderState === "pending"}
                      status={senderState}   // ✅ drives the icon
                      sx={{mt: 0.5, mb: 0.5 }}
                    >
                      Verify tx sender
                    </VerifyActionButton>
                  ) : (
                    <VerifyActionButton
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => verifyGenesisAnchor(auditChain)}
                      disabled={!provider || anchor.loading}
                      status={anchor.loading ? "pending" : (isAnchorRow ? anchor.ok : "unknown")}
                      sx={{ mt: 0.5, mb: 0.5 }}
                    >
                      Verify root match
                    </VerifyActionButton>
                  )}
                  {isGenesisRow && (
                    <>
                      {anchor.loading && (
                        <span
                          style={{
                            marginLeft: 12,
                            display: "inline-flex",
                            alignItems: "center",
                            background: "#eee",
                            color: "#888",
                            borderRadius: 16,
                            padding: "2px 16px",
                            fontWeight: 600,
                            fontSize: 15,
                            verticalAlign: "middle",
                          }}
                        >
                          <CircularProgress
                            size={16}
                            style={{ marginRight: 8 }}
                          />
                          Check…
                        </span>
                      )}

                      {!anchor.loading && isAnchorRow && anchor.ok === true && (
                        <span
                          style={{
                            marginLeft: 12,
                            display: "inline-block",
                            background: "#388e3c",
                            color: "#fff",
                            borderRadius: 16,
                            padding: "2px 16px",
                            fontWeight: 600,
                            fontSize: 15,
                            verticalAlign: "middle",
                          }}
                        >
                          ROOT MATCH
                        </span>
                      )}

                      {!anchor.loading && isAnchorRow && anchor.ok === false && (
                        <span
                          style={{
                            marginLeft: 12,
                            display: "inline-block",
                            background: "#d32f2f",
                            color: "#fff",
                            borderRadius: 16,
                            padding: "2px 16px",
                            fontWeight: 600,
                            fontSize: 15,
                            verticalAlign: "middle",
                          }}
                        >
                          ROOT MISMATCH
                        </span>
                      )}

                      {!anchor.loading && !isAnchorRow && (
                        <span
                          style={{
                            marginLeft: 12,
                            display: "inline-block",
                            background: "#9e9e9e",
                            color: "#fff",
                            borderRadius: 16,
                            padding: "2px 16px",
                            fontWeight: 600,
                            fontSize: 15,
                            verticalAlign: "middle",
                          }}
                        >
                          ROOT UNKNOWN
                        </span>
                      )}
                    </>
                  )}

                  {showProvButton  && (
                    <>
                      <VerifyActionButton
                        size="small"
                        variant="outlined"
                        color="secondary"
                        sx={{ mt: 0.5, mb: 0.5, ml: 1 }}
                        onClick={() => {
                          // Prefer in-memory decoded event for this reading_hash (from ProvenanceHome)
                          let event = pickEventByReadingHash(rh);

                          setProvenanceResults((prevMap) => ({
                            ...prevMap,
                            [rh]: "pending",
                          }));

                          setTimeout(async () => {
                            let eventToVerify = event;

                            const contractAddress =
                              process.env.REACT_APP_SOURCE_ERGONENERGY_CONTRACT_ADDRESS;
                            const txhFromDb = getTxHashFromItem(payload);
                            
                            // Prefer tx_hash from DB payload (works even when events are not loaded in memory)
                            if (!eventToVerify && provider && txhFromDb && contractAddress) {
                              if (!energyAbi || !energyAbi.abi) {
                                addLog && addLog("energyAbi.abi is not available, cannot fetch event args from tx hash.");
                              } else {
                                try {
                                  const fetched = await fetchEnergyEventArgsFromTxHash(
                                    provider,
                                    contractAddress,
                                    energyAbi.abi,
                                    txhFromDb,
                                    rh,
                                  );
                                  if (fetched?.args) {
                                    eventToVerify = { args: fetched.args };
                                    addLog &&
                                      addLog(
                                        `Fetched event from tx_receipt for readingHash ${rh} (tx=${txhFromDb}).`,
                                      );
                                  }
                                } catch (err) {
                                  addLog &&
                                    addLog(
                                      `Error decoding receipt for tx ${txhFromDb}: ${err.message || String(err)}`,
                                    );
                                }
                              }
                            }

                            // Fallback: try fetch by indexed readingHash topic (only works if readingHash is indexed)
                            if (!eventToVerify && provider) {
                              try {
                                if (!eventName) {
                                  addLog &&
                                    addLog(
                                      `Cannot determine event name/type for readingHash ${rh}. Skipping chain fetch.`,
                                    );
                                } else {
                                  const contractABI = energyAbi.abi;
                                  if (contractAddress && contractABI) {
                                    const contract = new Contract(
                                      contractAddress,
                                      contractABI,
                                      provider,
                                    );

                                    let eventFragment = null;
                                    try {
                                      eventFragment =
                                        contract.interface.getEvent(eventName);
                                    } catch {
                                      eventFragment = null;
                                    }

                                    if (eventFragment) {
                                      const topic =
                                        contract.interface.getEventTopic(
                                          eventFragment,
                                        );
                                      const logs = await provider.getLogs({
                                        address: contractAddress,
                                        topics: [
                                          topic,
                                          null,
                                          null,
                                          zeroPadValue(rh, 32),
                                        ],
                                        fromBlock: 0,
                                        toBlock: "latest",
                                      });

                                      if (logs && logs.length > 0) {
                                        const parsed =
                                          contract.interface.parseLog(logs[0]);
                                        eventToVerify = { args: parsed.args };
                                        addLog &&
                                          addLog(
                                            `Fetched blockchain event for readingHash ${rh} from chain.`,
                                          );
                                      } else {
                                        addLog &&
                                          addLog(
                                            `No blockchain event found for readingHash ${rh} on chain.`,
                                          );
                                      }
                                    }
                                  } else {
                                    addLog &&
                                      addLog(
                                        "Contract address or ABI missing for chain event fetch.",
                                      );
                                  }
                                }
                              } catch (err) {
                                addLog &&
                                  addLog(
                                    `Error fetching event from chain: ${err.message}`,
                                  );
                              }
                            }

                            if (eventToVerify) {
                              const ok =
                                await verifyProvenanceHashFromEvent(
                                  eventToVerify,
                                );
                              setProvenanceResults((prevMap) => ({
                                ...prevMap,
                                [rh]: ok,
                              }));
                            } else {
                              setProvenanceResults((prevMap) => ({
                                ...prevMap,
                                [rh]: "unknown",
                              }));
                              addLog &&
                                addLog(
                                  `provenanceHash NOT CHECKED for readingHash ${rh}: event not available (increase Sync range or ensure tx_hash is stored).`,
                                );
                            }
                          }, 0);
                        }}
                        disabled={provenanceResults[rh] === "pending"}
                        status={provenanceResults[rh]} // "pending" | true | false | "unknown" | "error" | null
                      >
                        Verify provenance_hash
                      </VerifyActionButton>

{/*
                      {provenanceResults[rh] === "pending" && (
                        <span
                          style={{
                            marginLeft: 12,
                            display: "inline-flex",
                            alignItems: "center",
                            background: "#eee",
                            color: "#888",
                            borderRadius: 16,
                            padding: "2px 16px",
                            fontWeight: 600,
                            fontSize: 15,
                            verticalAlign: "middle",
                          }}
                        >
                          <CircularProgress
                            size={16}
                            style={{ marginRight: 8 }}
                          />
                          Check…
                        </span>
                      )}

                      {typeof provenanceResults[rh] === "boolean" && (
                        <span
                          style={{
                            marginLeft: 12,
                            display: "inline-block",
                            background: provenanceResults[rh]
                              ? "#388e3c"
                              : "#d32f2f",
                            color: "#fff",
                            borderRadius: 16,
                            padding: "2px 16px",
                            fontWeight: 600,
                            fontSize: 15,
                            verticalAlign: "middle",
                          }}
                        >
                          {provenanceResults[rh] ? "MATCH" : "MISMATCH"}
                        </span>
                      )}
                      {provenanceResults[rh] === "unknown" && (
                        <span
                          title="Increase Sync range or ensure tx_hash is stored."
                          style={{
                            marginLeft: 12,
                            display: "inline-block",
                            background: "#9e9e9e",
                            color: "#fff",
                            borderRadius: 16,
                            padding: "2px 16px",
                            fontWeight: 600,
                            fontSize: 15,
                            verticalAlign: "middle",
                          }}
                        >
                          NOT CHECKED
                        </span>
                      )}

                      {provenanceResults[rh] === "error" && (
                        <span
                          style={{
                            marginLeft: 12,
                            display: "inline-block",
                            background: "#d32f2f",
                            color: "#fff",
                            borderRadius: 16,
                            padding: "2px 16px",
                            fontWeight: 600,
                            fontSize: 15,
                            verticalAlign: "middle",
                          }}
                        >
                          PROVENANCE ERROR
                        </span>
                      )}
*/}
                    </>
                  )}

                  <Divider sx={{ my: 1 }} />
                </li>
              );
            })}
          </ol>

          {(() => {
            const last = auditChain[auditChain.length - 1];
            const prev = last ? getPrevHashFromItem(last) : "";
            if (prev && isZeroHash(prev)) {
              return (
                <span style={{ display: "inline-block", marginTop: 8 }}>
                  <span
                    style={{
                      background: "#4caf50",
                      color: "#fff",
                      borderRadius: 4,
                      padding: "2px 8px",
                      fontWeight: 600,
                    }}
                  >
                    GENESIS
                  </span>
                </span>
              );
            }
            if (prev) {
              return (
                <span style={{ display: "inline-block", marginTop: 8 }}>
                  <span
                    style={{
                      background: "#f44336",
                      color: "#fff",
                      borderRadius: 4,
                      padding: "2px 8px",
                      fontWeight: 600,
                    }}
                  >
                    CHAIN ERROR
                  </span>
                </span>
              );
            }
            return null;
          })()}
        </Box>
      )}

      {/* Event Dialog */}
      <Dialog
        open={eventDialog.open}
        onClose={() => setEventDialog({ open: false, event: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Event Details</DialogTitle>
        <DialogContent>
          <pre style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>
            {eventDialog.event
              ? JSON.stringify(eventDialog.event, null, 2)
              : ""}
          </pre>

          {(() => {
            const ev = eventDialog.event;
            if (!ev) return null;

            // Try to resolve an in-memory chain event for verification first
            const unwrapped = unwrapApiPayload(ev) || ev;
            const m = extractMeterReading(unwrapped) || {};
            const rh = String(m.reading_hash ||
                m.readingHash ||
                ev.reading_hash ||
                ev.readingHash ||
                "");

            const chainEv = rh ? pickEventByReadingHash(rh) : null;

            if (!chainEv) return null;
            const hasProv = chainEv?.args && chainEv.args[3];
            if (!hasProv) return null;

            return (
              <Box sx={{ mt: 2 }}>
                <VerifyActionButton
                  size="small"
                  variant="contained"
                  color="secondary"
                  onClick={async () => {
                    const ok = await verifyProvenanceHashFromEvent(chainEv);
                    setEventDialog((prev) => ({
                      ...prev,
                      provenanceResult: ok,
                    }));
                  }}
                  status={provenanceResults[rh]}
                >
                  Verify provenanceHash
                </VerifyActionButton>
                {typeof eventDialog.provenanceResult === "boolean" ? (
                  <Typography
                    sx={{ ml: 2 }}
                    color={
                      eventDialog.provenanceResult
                        ? "success.main"
                        : "error.main"
                    }
                  >
                    {eventDialog.provenanceResult ? "MATCH" : "MISMATCH"}
                  </Typography>
                ) : null}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventDialog({ open: false, event: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Traverse Dialog */}
      <Dialog
        open={traverseDialog.open}
        onClose={() =>
          setTraverseDialog({ open: false, chain: [], error: null })
        }
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Hash Chain Traversal</DialogTitle>
        <DialogContent>
          {traverseDialog.error && (
            <Typography color="error">{traverseDialog.error}</Typography>
          )}
          <ol
            style={{ fontSize: 12, fontFamily: "monospace", paddingLeft: 20 }}
          >
            {traverseDialog.chain &&
              traverseDialog.chain.map((payload, idx) => {
                const p = unwrapApiPayload(payload) || {};
                const m = extractMeterReading(p) || {};
                const deviceId = String(p.device_id || p.deviceId || "");
                const rh = String(m.reading_hash || m.readingHash || "");
                const prev = String(m.previous_reading_hash || m.previousReadingHash || "");
                const ts = String(m.timestamp || p.timestamp || "");
                return (
                  <li key={rh || idx}>
                    <div>
                      <b>deviceId:</b> {deviceId}
                    </div>
                    <div>
                      <b>readingHash:</b> {rh}
                    </div>
                    <div>
                      <b>previous:</b> {prev}
                    </div>
                    <div>
                      <b>timestamp:</b> {ts}
                    </div>
                    <pre
                      style={{
                        fontSize: 11,
                        background: "#f5f5f5",
                        padding: 4,
                        borderRadius: 2,
                        color:
                          theme.palette.mode === "dark" ? "#222" : undefined,
                      }}
                    >
                      {JSON.stringify(p, null, 2)}
                    </pre>
                    <Divider sx={{ my: 1 }} />
                  </li>
                );
              })}
          </ol>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setTraverseDialog({ open: false, chain: [], error: null })
            }
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
